import { Editor } from "@tiptap/react";
import { nanoid } from "nanoid";
import queryString from "query-string";
import { useRecoilValue } from "recoil";
import { Instance } from "tippy.js";
import { UserAtom } from "../../../../atoms/UserAtom";
import { ITag } from "../../../../types/TagTypes";
import {
  getAnchorByURLAndSelect,
  restoreRange,
  selectAnchorElementURL,
  showURLTooltip,
} from "../../../../utils/LinkUtils";
import { useModal } from "../../../Modal/Modal";
import { selectionMarks } from "../../../NewEmail/TiptapAutoLink";
import LinkPopover from "./NoteLinkPopover";

export const FindLinkRegex =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(((&amp;)|(\{\{Contact\.\w+\}\})|[-a-zA-Z0-9()@:%_\+.~#?&//=])*)/;

function useLinkPopover(
  editor: Editor,
  tooltips: React.MutableRefObject<{ [href: string]: Instance }>
) {
  const modal = useModal();
  const user = useRecoilValue(UserAtom);

  const addInterestTag = (url: string, selectedTags: ITag[]): string => {
    let queriedURL = "";

    queriedURL = queryString.stringifyUrl({
      // url: interestTags.length ? interestTags[0].url : url,
      url,
      query: { __position__: nanoid() },
    });

    return queriedURL;
  };

  const restoreSelection = (
    hasAnchor: boolean,
    tooltipId?: string,
    newURL?: string,
    oldURL?: string,
    selectedText?: string,
    range?: Range
  ) => {
    if (hasAnchor) {
      if (newURL) {
        getAnchorByURLAndSelect(tooltipId);
        setTimeout(() => showURLTooltip(tooltipId, tooltips.current), 1000);
      } else {
        selectAnchorElementURL(tooltipId, tooltips.current);
      }
    } else if (selectedText) {
      restoreRange(range);
    }
  };

  const onSaveDecorator =
    (
      hasAnchor: boolean,
      selectedText: string,
      range: Range,
      tooltipId?: string
    ) =>
    (url: string, text: string, selectedTags: ITag[]) => {
      modal.popModal();

      const {
        state,
        state: { schema },
      } = editor;

      const newURL = addInterestTag(url, selectedTags);

      tooltipId ||= nanoid(); // Inserting a link

      let marks = [];

      if (!hasAnchor) {
        marks = selectionMarks(state, schema);
      } else {
        marks =
          (state.selection as any)?.node?.marks?.map(({ type }) => type.name) ||
          [];
      }

      const { from } = editor.state.selection;

      editor.commands.removeURL();
      editor.commands.addURL({
        marks,
        id: newURL,
        label: text,
        colorLink: user?.emailOptions.defaultModeColorLink,
        autoBoldLinks: !!user?.emailOptions.autoBoldLinks,
        tooltipId, // Updates the link if it already exists
      });

      // If it's a new anchor link, underline it by default,
      // if it's not a new anchor link, keep the current underline setting.
      if (!hasAnchor || marks.includes("underline"))
        editor.chain().setNodeSelection(from).setUnderline().run();
      if (marks.includes("italic"))
        editor.chain().setNodeSelection(from).setItalic().run();
      if (marks.includes("bold"))
        editor.chain().setNodeSelection(from).setBold().run();

      restoreSelection(
        true, // Inserting or updating a link
        tooltipId,
        newURL,
        undefined,
        selectedText,
        range
      );
    };

  const onCancel = (
    hasAnchor: boolean,
    oldURL: string,
    selectedText: string,
    range: Range,
    tooltipId?: string
  ) => {
    modal.popModal();
    restoreSelection(
      hasAnchor,
      tooltipId,
      undefined,
      oldURL,
      selectedText,
      range
    );
  };

  const openLinkModal = (
    hasAnchor: boolean,
    selectedText: string,
    range: Range,
    tooltipId?: string,
    linkURL?: string,
    linkLabel?: string
  ) => {
    if (!editor) return null;

    const isRawLink = FindLinkRegex.test(linkLabel || "");
    const sanitizedLinkURL = queryString
      .exclude(linkURL || "", ["__position__"])
      .trim();
    // const selectedStandardTags =
    //   newEmail.interestingURLs && newEmail.interestingURLs[linkURL || ""];

    modal.pushModal(
      <LinkPopover
        selectedText={selectedText}
        onSave={onSaveDecorator(hasAnchor, selectedText, range, tooltipId)}
        onCancel={() =>
          onCancel(hasAnchor, linkURL, selectedText, range, tooltipId)
        }
        hasAnchor={hasAnchor}
        isRawLink={isRawLink}
        linkURL={sanitizedLinkURL}
        linkLabel={linkLabel}
      />,
      "Edit Link",
      undefined,
      undefined,
      () => {
        restoreSelection(
          hasAnchor,
          tooltipId,
          undefined,
          linkURL,
          selectedText,
          range
        );
      }
    );
  };

  return { openLinkModal };
}

export default useLinkPopover;
